import {
  ICourseFrontEndModel,
  ICourseBackEndModel,
  IOtherFeesBackEndModel,
  IBranchBackEndModel,
} from "../types";
import { DurationUnit } from "../../../types";
import { HTMLTagRegEx } from "../../../constants";

export function parseToCourseBackEndModel(
  courseFrontEndModel: ICourseFrontEndModel,
): ICourseBackEndModel {
  // Branches -------------------------
  const branches: IBranchBackEndModel[] = courseFrontEndModel.branches.map(
    (branch) => ({ branchId: branch }),
  );
  Object.values(courseFrontEndModel.branchesWithAppCount || {}).forEach(
    (branch) => {
      if (
        branch.deactivationNote &&
        !branches.some((b) => b.branchId === branch.branchId)
      ) {
        branches.push({
          branchId: branch.branchId,
          deactivationNote: {
            text: branch.deactivationNote.text,
          },
        });
      }
    },
  );

  // Required and additional documents -------------------------
  const requiredDocs = courseFrontEndModel.requiredDocs.map((doc) => {
    const parsedDoc = { ...doc };
    delete parsedDoc.shortName;
    delete parsedDoc.longName;

    return parsedDoc;
  });

  Object.entries(courseFrontEndModel.additionalDocuments || {}).forEach(
    ([id, name]) => {
      if (!name.trim()) {
        return;
      }

      const doc: { id?: string; name: string } = { name: name.trim() };

      if (!id.startsWith("_")) {
        doc.id = id;
      }

      requiredDocs.push(doc);
    },
  );

  // Application Files -----------------------------------------
  const applicationForms = (courseFrontEndModel.applicationForms ?? []).map(
    (form) => ({
      fileId: form.id,
    }),
  );

  // Restricted Countries --------------------------------------
  const restrictedCountries = (
    courseFrontEndModel.restrictedCountries || []
  ).map((country) => country?.code);

  // Other Fees ------------------------------------------------
  const otherFees: IOtherFeesBackEndModel[] = [];

  [
    ...(courseFrontEndModel.mainOtherFees || []),
    ...(courseFrontEndModel.addedOtherFees || []),
  ].forEach((fee) => {
    if (fee?.name && fee?.amount) {
      otherFees.push({
        name: fee.name.trim(),
        amount: +fee.amount,
      });
    }
  });

  const courseBackEndModel: ICourseBackEndModel = {
    schoolId: courseFrontEndModel.schoolId,
    courseType: +courseFrontEndModel.courseType,
    category: courseFrontEndModel.category.trim(),
    minAge: +courseFrontEndModel.minAge,
    maxAge: courseFrontEndModel.maxAge?.trim()
      ? +courseFrontEndModel.maxAge
      : null,
    language: courseFrontEndModel.language.id,
    branches: branches,
    requiredDocs,
    applicationForms,
    applicationLink: courseFrontEndModel.applicationLink?.trim() ?? null,
    durationUnit: courseFrontEndModel.durationUnit.id,
    durationAmount: +courseFrontEndModel.durationAmount,
    housingTypes: courseFrontEndModel.housingTypes ?? [],
    restrictedCountries,
    price: +courseFrontEndModel.price,
    currency: courseFrontEndModel.currency.id,
    priceDurationUnit: courseFrontEndModel.priceDurationUnit.id,
    priceDurationAmount:
      courseFrontEndModel.priceDurationUnit.id === DurationUnit.FULL
        ? null
        : +courseFrontEndModel.priceDurationAmount!,
    commissionType: courseFrontEndModel.commissionType.id,
    commissionAmount: +courseFrontEndModel.commissionAmount,
    residenceAmount: courseFrontEndModel.residenceAmount?.trim()
      ? +courseFrontEndModel.residenceAmount
      : null,
    homestayAmount: courseFrontEndModel.homestayAmount?.trim()
      ? +courseFrontEndModel.homestayAmount
      : null,
    insuranceAmount: courseFrontEndModel.insuranceAmount?.trim()
      ? +courseFrontEndModel.insuranceAmount
      : null,
    otherFees,
    hasPriceDiscount: !!courseFrontEndModel.hasPriceDiscount,
    hasOtherFeesDiscount: !!courseFrontEndModel.hasOtherFeesDiscount,
    details: [],
    detailFiles: [],
  };

  if (courseFrontEndModel.id) {
    courseBackEndModel.id = courseFrontEndModel.id;
  }

  if (courseFrontEndModel.priceDiscountAmount?.trim()) {
    courseBackEndModel.priceDiscountAmount =
      +courseFrontEndModel.priceDiscountAmount;
  }

  if (courseFrontEndModel.priceDiscountType) {
    courseBackEndModel.priceDiscountType =
      courseFrontEndModel.priceDiscountType.id;
  }

  if (courseFrontEndModel.otherFeesDiscountAmount?.trim()) {
    courseBackEndModel.otherFeesDiscountAmount =
      +courseFrontEndModel.otherFeesDiscountAmount;
  }

  if (courseFrontEndModel.otherFeesDiscountType) {
    courseBackEndModel.otherFeesDiscountType =
      courseFrontEndModel.otherFeesDiscountType.id;
  }

  //Details ----------------------------------------------------
  Object.entries(courseFrontEndModel.details).forEach(([key, value]) => {
    const typeId = +key.split("_")[1];

    if (value && String(value).replace(HTMLTagRegEx, "").trim().length) {
      courseBackEndModel.details.push({
        type: typeId,
        content: String(value),
      });
    }
  });

  //Files ------------------------------------------------------
  if (courseFrontEndModel.detailFiles?.length) {
    courseBackEndModel.detailFiles = courseFrontEndModel.detailFiles.map(
      (file) => ({
        fileId: file.id,
      }),
    );
  } else {
    courseBackEndModel.detailFiles = [];
  }

  return courseBackEndModel;
}
